import React, { useState } from 'react';
import { formatDateRange, getNights } from '../../services/dateUtils';
import { BOOKING_STATUSES, USER_ROLES } from '../../constants/statuses';
import StatusBadge from '../common/StatusBadge';
import Button from '../common/Button';
import MessageModal from '../common/MessageModal';
import ReviewButton from '../reviews/ReviewButton';

const BookingDetails = ({
  booking,
  actionLoading,
  onEditDates,
  onCancel,
  onAccept,
  currentUserId,
  showCloseButton = false,
  onClose
}) => {
  const [showMessageModal, setShowMessageModal] = useState(false);

  const canEditDates = (booking) => {
    if (booking.current_user_role === USER_ROLES.HOST) {
      const allowedStatuses = [BOOKING_STATUSES.PENDING, BOOKING_STATUSES.ACCEPTED];

      // Check if host editing of in-progress bookings is enabled
      const allowHostEditInprogress = window.wwoofBooking?.settings?.allowHostEditInprogress || false;
      if (allowHostEditInprogress) {
        allowedStatuses.push(BOOKING_STATUSES.IN_PROGRESS);
      }

      return (
        allowedStatuses.includes(booking.status)
      );
    }

    return false;
  };

  const canCancel = (booking) => {
    const initiatedBy = booking.initiated_by || 'host';

    if (booking.current_user_role === USER_ROLES.HOST) {
      // Hosts can cancel pending or accepted bookings they initiated
      // For WWOOFer-initiated requests, they can also decline (cancel) when pending
      return [BOOKING_STATUSES.PENDING, BOOKING_STATUSES.ACCEPTED].includes(booking.status);
    } else if (booking.current_user_role === USER_ROLES.WWOOFER) {
      // WWOOFers can cancel pending bookings they initiated
      // For Host-initiated requests, they can decline (cancel) when pending
      return booking.status === BOOKING_STATUSES.PENDING;
    }
    return false;
  };

  const canAccept = (booking) => {
    if (booking.status !== BOOKING_STATUSES.PENDING) return false;

    const initiatedBy = booking.initiated_by || 'host';

    // WWOOFer-initiated: Host accepts
    if (initiatedBy === 'wwoofer') {
      return booking.current_user_role === USER_ROLES.HOST;
    }
    // Host-initiated: WWOOFer accepts
    return booking.current_user_role === USER_ROLES.WWOOFER;
  };

  const getCancelButtonLabel = (booking) => {
    const isPending = booking.status === BOOKING_STATUSES.PENDING;
    const initiatedBy = booking.initiated_by || 'host';
    
    if (isPending && initiatedBy === 'wwoofer') {
      if(booking.current_user_role === USER_ROLES.HOST)
        return 'Decline Request';
      else
        return 'Cancel Request';
    }

    if (isPending && initiatedBy === 'host') {
      if(booking.current_user_role === USER_ROLES.WWOOFER)
        return 'Decline Request';
      else
        return 'Cancel Request';
    } 
    return 'Cancel Booking';
    
  };

  const isHost = booking.current_user_role === USER_ROLES.HOST;
  const participantName = isHost ? booking.wwoofer_name : booking.host_name;
  const participantProfileUrl = isHost ? booking.wwoofer_profile_url : booking.host_profile_url;
  const participantAvatarUrl = isHost ? booking.wwoofer_avatar_url : booking.host_avatar_url;
  const nights = getNights(booking.start_date, booking.end_date);

  const getStatusDetail = (status) => {
    switch (status) {
      case BOOKING_STATUSES.PENDING:
        return 'Awaiting your response';
      case BOOKING_STATUSES.IN_PROGRESS:
        return 'Stay in progress';
      case BOOKING_STATUSES.CANCELLED:
        return 'Booking was cancelled';
      case BOOKING_STATUSES.COMPLETED:
        return 'Visit completed';
      default:
        return 'Booking confirmed';
    }
  };

  const formatCreatedAt = (createdAt) => {
    if (!createdAt) return '';
    return new Date(createdAt.replace(' ', 'T') + 'Z').toLocaleDateString(undefined, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  return (
    <div className='booking-card'>
      <div className='booking-header'>
        <div className='booking-date-block'>
          <span className='booking-date-icon'>📅</span>
          <div>
            <strong>{formatDateRange(booking.start_date, booking.end_date)}</strong>
            <span className='booking-duration'>
              {nights} {nights === 1 ? 'night' : 'nights'}
            </span>
          </div>
        </div>
        <StatusBadge status={booking.status} initiatedBy={booking.initiated_by} />
      </div>

      <div className='booking-participant'>
        {participantAvatarUrl && (
          <img className='booking-avatar' src={participantAvatarUrl} alt='' />
        )}
        <div className='booking-participant-info'>
          <span className='booking-with-label'>With</span>
          <a
            href={participantProfileUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="profile-link"
          >
            {participantName}
          </a>
          <a
            href={participantProfileUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="booking-profile-link"
          >
            View profile →
          </a>
        </div>
      </div>

      <div className='booking-meta'>
        <span>🌱 {booking.initiated_by === 'wwoofer' ? 'WWOOFer Request' : 'Host Request'}</span>
        {booking.created_at && (
          <span>Created {formatCreatedAt(booking.created_at)}</span>
        )}
      </div>

      <div className='booking-status-detail'>
        <span className='booking-status-label'>{getStatusDetail(booking.status)}</span>
      </div>

      <div className='booking-actions'>
        {canEditDates(booking) && (
          <Button
            variant='edit'
            disabled={actionLoading}
            onClick={() => onEditDates(booking)}
          >
            {actionLoading ? '...' : '✎ Edit Dates'}
          </Button>
        )}

        {canCancel(booking) && (
          <Button
            variant='cancel'
            disabled={actionLoading}
            onClick={() => onCancel(booking.id)}
          >
            {actionLoading ? '...' : getCancelButtonLabel(booking)}
          </Button>
        )}

        {canAccept(booking) && (
          <Button
            variant='accept'
            disabled={actionLoading}
            onClick={() => onAccept(booking)}
          >
            {actionLoading ? '...' : '✓ Accept'}
          </Button>
        )}

        <Button
          onClick={() => setShowMessageModal(true)}
          className="message-action"
        >
          💬 View Messages
        </Button>

        <ReviewButton
          booking={booking}
          currentUserId={currentUserId}
        />

        {showCloseButton && (
          <Button onClick={onClose}>
            Close
          </Button>
        )}
      </div>

      {showMessageModal && (
        <MessageModal
          isOpen={showMessageModal}
          onClose={() => setShowMessageModal(false)}
          conversationId={booking.pm_thread_id}
          currentUserId={currentUserId}
          nonce={window.wwoofBooking?.nonce || window.wwoofBooking?.nonce || ''}
          onSendMessage={() => {
            // Optional callback for when a message is sent
            console.log('Message sent');
          }}
        />
      )}
    </div>
  );
};

export default BookingDetails;
